import { useCallback, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { SwipeCard, CardProfile } from './SwipeCard'

interface SwipeStackProps {
  profiles: CardProfile[]
  onLike: (profile: CardProfile) => Promise<void>
  onSkip: (profile: CardProfile) => Promise<void>
  isLoading?: boolean
}

export function SwipeStack({
  profiles,
  onLike,
  onSkip,
  isLoading = false,
}: SwipeStackProps) {
  const top = profiles[0]
  const [actionBusy, setActionBusy] = useState(false)
  const actionBusyRef = useRef(false)

  const runSkip = useCallback(async () => {
    if (!top || actionBusyRef.current) return
    actionBusyRef.current = true
    setActionBusy(true)
    try {
      await onSkip(top)
    } finally {
      actionBusyRef.current = false
      setActionBusy(false)
    }
  }, [top, onSkip])

  const runLike = useCallback(async () => {
    if (!top || actionBusyRef.current) return
    actionBusyRef.current = true
    setActionBusy(true)
    try {
      await onLike(top)
    } finally {
      actionBusyRef.current = false
      setActionBusy(false)
    }
  }, [top, onLike])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Загружаем анкеты...</p>
      </div>
    )
  }

  if (!top) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4 text-center">
        <div className="mb-4 text-6xl">🐱</div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Анкеты закончились</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Загляни на главный экран позже — появятся новые люди.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <div className="relative min-h-0 flex-1">
        <AnimatePresence mode="wait">
          <SwipeCard
            key={top.userId}
            profile={top}
            onLike={() => onLike(top)}
            onSkip={() => onSkip(top)}
          />
        </AnimatePresence>
      </div>
      <div className="mt-2 flex shrink-0 justify-center gap-2 px-1 pb-0.5">
        <button
          type="button"
          disabled={actionBusy}
          onClick={() => void runSkip()}
          className="min-h-[44px] min-w-[7.5rem] rounded-full border-2 border-orange-500 bg-orange-50 px-4 py-2 text-sm font-bold text-orange-800 shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 dark:border-orange-600 dark:bg-orange-950/40 dark:text-orange-100"
        >
          Пропуск
        </button>
        <button
          type="button"
          disabled={actionBusy}
          onClick={() => void runLike()}
          className="min-h-[44px] min-w-[7.5rem] rounded-full border-2 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-900 shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 dark:border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-100"
        >
          Симпатия
        </button>
      </div>
    </div>
  )
}
